import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import CreatableSelect from '@atlaskit/select/CreatableSelect'
import Select, { AsyncSelect } from '@atlaskit/select'
import Button from '@atlaskit/button'
import Avatar from '@atlaskit/avatar'
import { RemovableTag } from '@atlaskit/tag'
import TagGroup from '@atlaskit/tag-group'
import { token } from '@atlaskit/tokens'
import { getTagColor } from '../../utils/tagColors'
import {
  updateTestScenarioAsync,
  selectProjectLabels,
  selectTestScenarioLabels,
  selectTestScenarioDetails,
  addTestScenarioLabelAsync,
  removeTestScenarioLabelAsync,
  selectLinkedJiraIssues,
  linkJiraIssueAsync,
  unlinkJiraIssueAsync
} from '../../store/slices/testScenarioSlice'
import { searchJiraUsers } from '../../utils/jiraContext'
import { useNotification } from '../../contexts/NotificationContext'
import { InlineEditField } from '../common/InlineEditField'
import { JiraIssuePicker } from '../common/JiraIssuePicker'
import LinkIcon from '@atlaskit/icon/glyph/link'
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import Tooltip from '@atlaskit/tooltip'

/**
 * TEST SCENARIO EDIT MODAL
 *
 * Modal for editing test scenario details using Jira-style inline edit.
 * All fields save immediately when changed (no Save button).
 *
 * Features:
 * - Inline edit for name and description fields
 * - Priority dropdown saves immediately on selection
 * - Assignee picker saves immediately on selection
 * - Labels save immediately on add/remove
 * - Close button to close modal
 */
export function TestScenarioEditModal({ isOpen, onClose, testScenario, isLibrary = false }) {
  const dispatch = useDispatch()
  const { showError } = useNotification()

  // Get live test scenario data from Redux store so edits are reflected immediately
  const liveTestScenario = useSelector(selectTestScenarioDetails)

  // Select portal target for proper rendering in modal
  const portalTarget = useMemo(
    () => (typeof document !== 'undefined' ? document.body : null),
    []
  )

  // Select styles for z-index in modal
  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    }),
    []
  )

  // Label state and selectors
  const projectLabels = useSelector(selectProjectLabels)
  const testScenarioLabels = useSelector(selectTestScenarioLabels)

  // Linked Jira issues state
  const linkedJiraIssues = useSelector(selectLinkedJiraIssues)

  // Local state for select fields (they need controlled state for UI)
  const [priority, setPriority] = useState(null)
  const [localLabels, setLocalLabels] = useState([])
  const [localAssignee, setLocalAssignee] = useState(null)
  const [localLinkedIssues, setLocalLinkedIssues] = useState([])
  const [selectedIssue, setSelectedIssue] = useState(null)
  const [isLinking, setIsLinking] = useState(false)

  // Priority options
  const priorityOptions = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' }
  ]

  // Unassigned option for clearing assignee
  const UNASSIGNED_OPTION = {
    label: 'Unassigned',
    value: null,
    avatarUrl: null,
    isUnassigned: true
  }

  // Initialize priority when liveTestScenario changes
  useEffect(() => {
    if (liveTestScenario) {
      const currentPriority = priorityOptions.find(
        opt => opt.value === liveTestScenario.testScenarioPriority
      ) || priorityOptions[1]
      setPriority(currentPriority)
    }
  }, [liveTestScenario])

  // Sync local labels with Redux when modal opens
  useEffect(() => {
    setLocalLabels(testScenarioLabels)
  }, [testScenarioLabels, isOpen])

  // Sync local linked issues with Redux when modal opens
  useEffect(() => {
    setLocalLinkedIssues(linkedJiraIssues)
  }, [linkedJiraIssues, isOpen])

  // Sync local assignee when liveTestScenario changes
  useEffect(() => {
    if (liveTestScenario?.testScenarioAssignee) {
      const assignee = liveTestScenario.testScenarioAssignee
      setLocalAssignee({
        label: assignee.displayName
          || (assignee.accountId ? 'Loading user...' : 'Unknown User'),
        value: assignee.accountId,
        avatarUrl: assignee.avatarUrl
      })
    } else {
      setLocalAssignee(null)
    }
  }, [liveTestScenario, isOpen])

  /**
   * HANDLE NAME CONFIRM
   * Saves the scenario name immediately when user confirms edit.
   */
  const handleNameConfirm = useCallback(async (newValue) => {
    if (!liveTestScenario) return

    try {
      await dispatch(updateTestScenarioAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        updates: { scenarioName: newValue.trim() }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update scenario name:', error)
      const errorMessage = error?.message || 'Failed to update scenario name. Please try again.'
      showError('Update Failed', errorMessage)
      throw error
    }
  }, [dispatch, liveTestScenario, showError])

  /**
   * HANDLE DESCRIPTION CONFIRM
   * Saves the scenario description immediately when user confirms edit.
   */
  const handleDescriptionConfirm = useCallback(async (newValue) => {
    if (!liveTestScenario) return

    try {
      await dispatch(updateTestScenarioAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        updates: { description: newValue.trim() }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update scenario description:', error)
      const errorMessage = error?.message || 'Failed to update description. Please try again.'
      showError('Update Failed', errorMessage)
      throw error
    }
  }, [dispatch, liveTestScenario, showError])

  /**
   * HANDLE PRIORITY CHANGE
   * Updates priority immediately on selection (no Save button needed)
   */
  const handlePriorityChange = async (selectedOption) => {
    if (!liveTestScenario) return

    // Update local state for immediate UI feedback
    setPriority(selectedOption)

    try {
      await dispatch(updateTestScenarioAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        updates: { priority: selectedOption.value }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update priority:', error)
      showError('Update Failed', 'Failed to update priority')
      // Revert to previous value on error
      const previousPriority = priorityOptions.find(
        opt => opt.value === liveTestScenario.testScenarioPriority
      ) || priorityOptions[1]
      setPriority(previousPriority)
    }
  }

  /**
   * LOAD USER OPTIONS
   * Async function to fetch Jira users based on search query.
   */
  const loadUserOptions = useCallback(async (inputValue) => {
    const users = await searchJiraUsers(inputValue)
    const userOptions = users.map(user => ({
      label: user.displayName,
      value: user.accountId,
      avatarUrl: user.avatarUrl
    }))
    return [UNASSIGNED_OPTION, ...userOptions]
  }, [])

  /**
   * FORMAT USER OPTION
   * Custom render for user options showing avatar + name.
   */
  const formatUserOption = useCallback(({ label, avatarUrl, isUnassigned }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isUnassigned ? (
        <Avatar size="small" name="?" />
      ) : (
        <Avatar size="small" src={avatarUrl} name={label} />
      )}
      <span style={isUnassigned ? { color: '#6B778C', fontStyle: 'italic' } : undefined}>
        {label}
      </span>
    </div>
  ), [])

  /**
   * LABEL OPTIONS
   * Transform labels to CreatableSelect format.
   * Filters out already-selected labels to prevent duplicates.
   */
  const labelOptions = useMemo(() => {
    const selectedIds = new Set(localLabels.map(l => l.id))
    return projectLabels
      .filter(label => !selectedIds.has(label.id))
      .map(label => ({
        label: label.name,
        value: label.id
      }))
  }, [projectLabels, localLabels])

  /**
   * HANDLE ASSIGNEE CHANGE
   * Updates assignee immediately (no save button needed)
   */
  const handleAssigneeChange = async (selectedOption) => {
    if (!liveTestScenario) return

    const isUnassigning = !selectedOption || selectedOption.value === null
    setLocalAssignee(isUnassigning ? null : selectedOption)

    const assigneeForApi = isUnassigning ? '' : selectedOption.value
    const assigneeForRedux = isUnassigning ? null : {
      accountId: selectedOption.value,
      displayName: selectedOption.label,
      avatarUrl: selectedOption.avatarUrl
    }

    try {
      await dispatch(updateTestScenarioAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        updates: {
          assignee: assigneeForApi,
          _assigneeObject: assigneeForRedux
        }
      })).unwrap()
    } catch (error) {
      console.error('Failed to update assignee:', error)
      showError('Update Failed', 'Failed to update assignee')
    }
  }

  /**
   * HANDLE LABEL SELECT
   * Handles adding a label when user selects from dropdown or creates a new one.
   * The select operates as a single-action input (not isMulti), so it receives
   * a single option. After adding, the select resets to empty.
   */
  const handleLabelSelect = async (selectedOption) => {
    if (!liveTestScenario || !selectedOption) return

    if (selectedOption.__isNew__) {
      // CREATE NEW LABEL flow
      const labelData = { labelName: selectedOption.label }
      const tempLabel = { id: `temp-${Date.now()}`, name: selectedOption.label }

      // Optimistic: add to local state immediately
      setLocalLabels(prev => [...prev, tempLabel])

      try {
        const result = await dispatch(addTestScenarioLabelAsync({
          testScenarioId: liveTestScenario.testScenarioId,
          labelData
        })).unwrap()
        // Replace temp label with real server response
        setLocalLabels(prev => prev.map(l => l.id === tempLabel.id ? result : l))
      } catch (error) {
        // Rollback: remove the temp label
        setLocalLabels(prev => prev.filter(l => l.id !== tempLabel.id))
        showError('Label Error', 'Failed to create label')
      }
    } else {
      // ADD EXISTING LABEL flow
      const labelData = { labelId: selectedOption.value }
      const newLabel = { id: selectedOption.value, name: selectedOption.label }

      // Optimistic: add to local state immediately
      setLocalLabels(prev => [...prev, newLabel])

      try {
        await dispatch(addTestScenarioLabelAsync({
          testScenarioId: liveTestScenario.testScenarioId,
          labelData
        })).unwrap()
      } catch (error) {
        // Rollback: remove the label
        setLocalLabels(prev => prev.filter(l => l.id !== newLabel.id))
        showError('Label Error', 'Failed to add label')
      }
    }
  }

  /**
   * HANDLE LABEL REMOVE
   * Called when user clicks the remove button on a Tag.
   * Uses optimistic update with rollback on failure.
   *
   * @param {string} labelId - The ID of the label to remove
   * @param {string} labelName - The name of the label (for rollback)
   */
  const handleLabelRemove = async (labelId, labelName) => {
    if (!liveTestScenario) return

    // Optimistic: remove from local state immediately
    setLocalLabels(prev => prev.filter(l => l.id !== labelId))

    try {
      await dispatch(removeTestScenarioLabelAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        labelId
      })).unwrap()
    } catch (error) {
      // Rollback: re-add the label
      const restoredLabel = { id: labelId, name: labelName }
      setLocalLabels(prev => [...prev, restoredLabel])
      showError('Label Error', 'Failed to remove label')
    }
  }

  /**
   * HANDLE LINK JIRA ISSUE
   * Links selected Jira issue to the scenario. Optimistic update with rollback.
   */
  const handleLinkJiraIssue = async () => {
    if (!liveTestScenario || !selectedIssue) return

    const jiraIssueKey = selectedIssue.value

    // Check for duplicate
    if (localLinkedIssues.find(l => l.jiraIssueKey === jiraIssueKey)) {
      showError('Already Linked', `${jiraIssueKey} is already linked to this scenario`)
      return
    }

    // Optimistic: add temp entry to local state
    const tempLink = { id: `temp-${Date.now()}`, jiraIssueKey, linkedOn: new Date().toISOString() }
    setLocalLinkedIssues(prev => [...prev, tempLink])
    setSelectedIssue(null)
    setIsLinking(true)

    try {
      const result = await dispatch(linkJiraIssueAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        jiraIssueKey
      })).unwrap()
      // Replace temp with real server response
      setLocalLinkedIssues(prev => prev.map(l => l.id === tempLink.id ? result : l))
    } catch (error) {
      // Rollback: remove the temp entry
      setLocalLinkedIssues(prev => prev.filter(l => l.id !== tempLink.id))
      showError('Link Failed', error?.message || 'Failed to link Jira issue')
    } finally {
      setIsLinking(false)
    }
  }

  /**
   * HANDLE UNLINK JIRA ISSUE
   * Removes a linked Jira issue. Optimistic update with rollback.
   */
  const handleUnlinkJiraIssue = async (linkId, jiraIssueKey) => {
    if (!liveTestScenario) return

    // Optimistic: remove from local state
    const removedItem = localLinkedIssues.find(l => l.id === linkId)
    setLocalLinkedIssues(prev => prev.filter(l => l.id !== linkId))

    try {
      await dispatch(unlinkJiraIssueAsync({
        testScenarioId: liveTestScenario.testScenarioId,
        linkId
      })).unwrap()
    } catch (error) {
      // Rollback: re-add the removed item
      if (removedItem) {
        setLocalLinkedIssues(prev => [...prev, removedItem])
      }
      showError('Unlink Failed', error?.message || 'Failed to unlink Jira issue')
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - centered with flexbox */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '680px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {liveTestScenario && (
            <>
              {/* Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid var(--ds-border, #DFE1E6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0
                }}
              >
                {/* Left: Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  <Dialog.Title
                    style={{
                      fontSize: '20px',
                      fontWeight: 500,
                      color: 'var(--ds-text, #172B4D)',
                      margin: 0
                    }}
                  >
                    Edit Test Scenario
                  </Dialog.Title>
                </div>

                {/* Right: Close button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <Tooltip content="Close">
                    <Button
                      appearance="subtle"
                      iconBefore={<CrossIcon label="Close" size="medium" />}
                      onClick={onClose}
                    />
                  </Tooltip>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '20px 24px' }}>
                {/* Scenario Name - Required, inline edit */}
                <InlineEditField
                  label="Scenario Name"
                  value={liveTestScenario.testScenarioName || ''}
                  onConfirm={handleNameConfirm}
                  placeholder="Enter scenario name"
                  required
                  requiredMessage="Scenario name is required"
                />

                {/* Description - Optional, inline edit multiline */}
                <InlineEditField
                  label="Description"
                  value={liveTestScenario.testScenarioDescription || ''}
                  onConfirm={handleDescriptionConfirm}
                  placeholder="Add a description for this scenario"
                  multiline
                  rows={3}
                />

                {/* Priority - Saves immediately on selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: token('color.text.subtle', '#5E6C84'),
                      marginBottom: '8px'
                    }}
                  >
                    Priority
                  </label>
                  <Select
                    options={priorityOptions}
                    value={priority}
                    onChange={handlePriorityChange}
                    placeholder="Select priority"
                    menuPortalTarget={portalTarget}
                    menuPlacement="auto"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    styles={selectStyles}
                  />
                </div>

                {/* Assignee - Saves immediately on selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: token('color.text.subtle', '#5E6C84'),
                      marginBottom: '8px'
                    }}
                  >
                    Assignee
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={[UNASSIGNED_OPTION]}
                    loadOptions={loadUserOptions}
                    value={localAssignee}
                    onChange={handleAssigneeChange}
                    placeholder="Search for a user..."
                    isClearable
                    formatOptionLabel={formatUserOption}
                    menuPortalTarget={portalTarget}
                    menuPlacement="auto"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    styles={selectStyles}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `No users found for "${inputValue}"` : 'Start typing to search users'
                    }
                  />
                </div>

                {/* Labels - Saves immediately on add/remove */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: token('color.text.subtle', '#5E6C84'),
                      marginBottom: '8px'
                    }}
                  >
                    Labels
                  </label>

                  {/* Label search/create input - single-action, clears after each selection */}
                  <CreatableSelect
                    options={labelOptions}
                    value={null}
                    onChange={handleLabelSelect}
                    placeholder="Search or create labels..."
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    menuPortalTarget={portalTarget}
                    menuPlacement="auto"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    styles={selectStyles}
                    noOptionsMessage={() => 'Type to create a new label'}
                  />

                  {/* Applied labels displayed as native Atlaskit Tags */}
                  {localLabels.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <TagGroup label="Applied labels">
                        {localLabels.map((label) => (
                          <RemovableTag
                            key={label.id}
                            text={label.name}
                            color={getTagColor(label.name)}
                            appearance="rounded"
                            removeButtonLabel={`Remove ${label.name}`}
                            onAfterRemoveAction={() => handleLabelRemove(label.id, label.name)}
                          />
                        ))}
                      </TagGroup>
                    </div>
                  )}
                </div>

                {/* Linked Jira Issues - Only shown for release scenarios, hidden for library templates */}
                {!isLibrary && (
                <div style={{ marginTop: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: token('color.text.subtle', '#5E6C84'),
                      marginBottom: '8px'
                    }}
                  >
                    Jira Issues
                  </label>

                  {/* Issue search + Link button row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <JiraIssuePicker
                        value={selectedIssue}
                        onChange={setSelectedIssue}
                        placeholder="Search for a Jira issue to link..."
                        isDisabled={isLinking}
                      />
                    </div>
                    <Button
                      appearance="primary"
                      onClick={handleLinkJiraIssue}
                      isLoading={isLinking}
                      isDisabled={!selectedIssue}
                      iconBefore={!isLinking ? <LinkIcon size="small" label="Link" /> : undefined}
                      style={{ marginTop: '2px' }}
                    >
                      Link
                    </Button>
                  </div>

                  {/* Display linked issues as removable items */}
                  {localLinkedIssues.length > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {localLinkedIssues.map((link) => (
                        <div
                          key={link.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                            fontSize: '13px'
                          }}
                        >
                          <span style={{
                            fontWeight: 500,
                            color: 'var(--ds-link, #0065FF)'
                          }}>
                            {link.jiraIssueKey}
                          </span>
                          <button
                            onClick={() => handleUnlinkJiraIssue(link.id, link.jiraIssueKey)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--ds-icon-subtle, #6B778C)'
                            }}
                            title={`Unlink ${link.jiraIssueKey}`}
                          >
                            <EditorCloseIcon size="small" label="Remove" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Helper text */}
                  <div style={{
                    marginTop: '6px',
                    fontSize: '11px',
                    color: token('color.text.subtlest', '#7A869A')
                  }}>
                    Link Jira issues to track requirements, bugs, or stories related to this scenario
                  </div>
                </div>
                )}
              </div>

              {/* Footer - Single Close button */}
              <div
                style={{
                  padding: '16px 24px',
                  borderTop: '1px solid var(--ds-border, #DFE1E6)',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  appearance="primary"
                  onClick={onClose}
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
